// src/components/instructor/InstructorVideoQueue.js
//
// Pass 71 — the focused instructor Video Queue (reads ONLY the `videos` collection; replaced mounting the giant
// legacy InstructorDashboard). Pass 71A hardened tenancy:
//   • AVAILABLE is now SELECTED-ORG scoped (no org selected → no global pool; a tenantless legacy video is never
//     offered for claim — Firestore Rules enforce the same, this is the matching UI).
//   • MY REVIEWS shows org-scoped assignments for the selected org PLUS legacy explicit personal assignments on
//     videos that carry no organizationId (so current work never silently disappears), flagged "Legacy".
//   • A conflicting-assignment row gets NO instructor CTA (needs operator resolution); an assigned-but-incomplete
//     upload gets a disabled state, not a broken editor link.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import {
  getVideoTitle,
  getVideoWorkflowState,
  hasVideoAssignmentConflict,
  isAvailableToClaim,
  isUploadComplete,
  isVideoAssignedToActor,
} from '../../utils/videoWorkflow';
import { claimVideoForInstructor } from '../../utils/videoClaim';

const C = {
  card: '#FFFFFF', card2: '#F5F4F0', border: 'rgba(0,0,0,0.06)',
  green: '#16A34A', blue: '#2563EB', orange: '#F05A1F', red: '#DC2626', amber: '#D97706',
  t1: '#1C1B18', t2: '#6B6B63', t3: '#A3A299',
};
const font = "'DM Sans',sans-serif";
const str = (v) => (v == null ? '' : String(v).trim());

const STATE_BADGE = {
  reviewed: { label: 'Reviewed', color: C.green, bg: 'rgba(34,197,94,0.10)' },
  in_review: { label: 'In review', color: C.blue, bg: 'rgba(37,99,235,0.10)' },
  uploaded: { label: 'Awaiting review', color: C.orange, bg: 'rgba(240,90,31,0.10)' },
  uploading: { label: 'Uploading', color: C.t2, bg: 'rgba(0,0,0,0.05)' },
  failed: { label: 'Upload failed', color: C.red, bg: 'rgba(220,38,38,0.10)' },
};

function eventMs(video) {
  const ts = video?.createdAt;
  if (ts?.seconds) return ts.seconds * 1000;
  if (typeof ts === 'number') return ts;
  const parsed = ts ? Date.parse(ts) : NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

function ageLabel(video) {
  const ms = eventMs(video);
  if (!ms) return '';
  const days = (Date.now() - ms) / 86_400_000;
  if (days < 1) return 'Today';
  if (days < 2) return 'Yesterday';
  if (days < 7) return `${Math.round(days)} days ago`;
  return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function dedupeById(list) {
  const byId = new Map();
  list.forEach((v) => { if (v?.id && !byId.has(v.id)) byId.set(v.id, v); });
  return [...byId.values()];
}

function InstructorVideoQueue({ organizationId = '' }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || '';
  const orgId = str(organizationId);

  const [assignedModern, setAssignedModern] = useState(null); // null = still loading
  const [assignedLegacy, setAssignedLegacy] = useState(null);
  const [available, setAvailable] = useState(orgId ? null : []);
  const [error, setError] = useState('');
  const [claimingId, setClaimingId] = useState('');

  useEffect(() => {
    if (!uid) return undefined;
    setError('');
    const map = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const onErr = (e) => { console.error('Video queue query failed:', e); setError('We could not load your video queue right now.'); };

    // Pass 72 (§3) — MY REVIEWS is SELECTED-ORG scoped. An unscoped `assignedInstructorId==uid` query would be
    // Rules-rejected if ANY returned doc were org-scoped to an org this instructor isn't staff of (Firestore
    // fails the whole query if one returned doc fails the read rule). Adding organizationId==selectedOrg keeps
    // every returned doc within the org the caller is staff of. Legacy no-org assigned videos are therefore not
    // listed here — they await Pass-72 tenant reconciliation (fail closed, §4). No selected org → no queries.
    let unsubModern = () => {}; let unsubLegacy = () => {}; let unsubAvail = () => {};
    if (orgId) {
      unsubModern = onSnapshot(query(collection(db, 'videos'), where('assignedInstructorId', '==', uid), where('organizationId', '==', orgId)), (s) => setAssignedModern(map(s)), onErr);
      unsubLegacy = onSnapshot(query(collection(db, 'videos'), where('assignedTo', '==', uid), where('organizationId', '==', orgId)), (s) => setAssignedLegacy(map(s)), onErr);
      // AVAILABLE — SELECTED-ORG scoped claimable worklist.
      // Pass 72 — include uploadStatus=='complete' so a still-'uploading' modern video (which shares
      // pending/unassigned/real) is NOT returned; an instructor can't read a non-claimable doc, and one such
      // doc in the result would fail the whole list query. Org-scoped videos are all modern (have uploadStatus).
      unsubAvail = onSnapshot(
        query(
          collection(db, 'videos'),
          where('organizationId', '==', orgId),
          where('assignedTo', '==', null),
          where('assigned', '==', false),
          where('coachType', '==', 'real'),
          where('status', '==', 'pending'),
          where('uploadStatus', '==', 'complete'),
        ),
        (s) => setAvailable(map(s)),
        onErr,
      );
    } else {
      setAssignedModern([]); setAssignedLegacy([]); setAvailable([]);
    }
    return () => { unsubModern(); unsubLegacy(); unsubAvail(); };
  }, [uid, orgId]);

  const loading = assignedModern === null || assignedLegacy === null || available === null;

  const myReviews = useMemo(() => {
    const merged = dedupeById([...(assignedModern || []), ...(assignedLegacy || [])])
      .filter((v) => isVideoAssignedToActor(v, uid) && getVideoWorkflowState(v) !== 'reviewed')
      // org-scoped: a video WITH an org shows only for the selected org; a legacy no-org video stays (flagged).
      .filter((v) => (str(v.organizationId) ? str(v.organizationId) === orgId : true));
    return merged.sort((a, b) => eventMs(a) - eventMs(b)); // oldest awaiting review first
  }, [assignedModern, assignedLegacy, uid, orgId]);

  const availableReviews = useMemo(() => {
    return (available || [])
      .filter(isAvailableToClaim)
      .sort((a, b) => eventMs(a) - eventMs(b)); // oldest valid pending first
  }, [available]);

  const handleClaim = async (video) => {
    if (claimingId) return;
    setClaimingId(video.id);
    setError('');
    try {
      await claimVideoForInstructor(db, video.id, { actorAuthUserId: uid, organizationId: orgId });
      navigate(`/video/${video.id}/editor`);
    } catch (e) {
      setError(e?.message || 'We could not claim this video right now.');
      setClaimingId('');
    }
  };

  const openEditor = (video) => navigate(`/video/${video.id}/editor`);

  if (!uid) return null;

  return (
    <div style={{ fontFamily: font, color: C.t1 }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: 0.3 }}>Video Queue</div>
        <div style={{ fontSize: 13, color: C.t3, marginTop: 2 }}>Review student videos and claim work waiting for a coach.</div>
      </div>

      {error ? (
        <div role="alert" style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', color: C.red, borderRadius: 12, padding: '12px 16px', fontSize: 13, marginBottom: 16 }}>{error}</div>
      ) : null}

      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: C.t3, fontSize: 14 }}>Loading your video queue…</div>
      ) : (
        <>
          <Section title="My reviews" count={myReviews.length} emptyText="No videos are assigned to you right now.">
            {myReviews.map((v) => <VideoRow key={v.id} video={v} onOpen={() => openEditor(v)} />)}
          </Section>

          <Section
            title="Available to claim"
            count={availableReviews.length}
            emptyText={orgId
              ? 'No unclaimed videos are waiting for this organisation.'
              : 'Select an organisation to see videos available to claim.'}
          >
            {availableReviews.map((v) => (
              <VideoRow key={v.id} video={v} claimable claiming={claimingId === v.id} disabledAll={Boolean(claimingId)} onClaim={() => handleClaim(v)} />
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, count, emptyText, children }) {
  const isEmpty = React.Children.count(children) === 0;
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: C.t2 }}>{title}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.t3, background: C.card2, borderRadius: 20, padding: '2px 9px' }}>{count}</span>
      </div>
      {isEmpty ? (
        <div style={{ padding: '18px 0', color: C.t3, fontSize: 13 }}>{emptyText}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
      )}
    </div>
  );
}

function VideoRow({ video, onOpen, onClaim, claimable, claiming, disabledAll }) {
  const state = getVideoWorkflowState(video);
  const badge = STATE_BADGE[state] || STATE_BADGE.uploaded;
  const conflict = hasVideoAssignmentConflict(video);
  const uploadComplete = isUploadComplete(video);
  const legacyNoOrg = !str(video.organizationId);
  const sport = video.sport || '';

  // Pass 71A — a conflicting record gets NO instructor action; an assigned-but-incomplete upload can't be reviewed.
  let action = null;
  if (conflict) {
    action = <Tag color={C.amber} bg="rgba(217,119,6,0.10)">Needs operator resolution</Tag>;
  } else if (claimable) {
    action = <ActionBtn disabled={disabledAll} onClick={onClaim}>{claiming ? 'Claiming…' : 'Claim & review'}</ActionBtn>;
  } else if (!uploadComplete) {
    action = <Tag color={C.t3} bg={C.card2}>Upload incomplete</Tag>;
  } else {
    action = <ActionBtn onClick={onOpen}>{state === 'in_review' ? 'Continue review' : 'Review'}</ActionBtn>;
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 16px' }}>
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getVideoTitle(video)}</div>
        <div style={{ fontSize: 12, color: C.t3, marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {sport ? <span>{sport}</span> : null}
          <span>{ageLabel(video)}</span>
          {legacyNoOrg ? <span title="This video predates organisation tagging" style={{ color: C.t3 }}>Legacy · tenant not recorded</span> : null}
        </div>
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: badge.color, background: badge.bg, borderRadius: 20, padding: '4px 10px', whiteSpace: 'nowrap' }}>{badge.label}</span>
      {conflict ? <Tag color={C.amber} bg="rgba(217,119,6,0.10)">Assignment conflict</Tag> : null}
      <div style={{ flex: '0 0 auto' }}>{action}</div>
    </div>
  );
}

function Tag({ color, bg, children }) {
  return <span style={{ fontSize: 11, fontWeight: 700, color, background: bg, borderRadius: 20, padding: '6px 12px', whiteSpace: 'nowrap' }}>{children}</span>;
}
function ActionBtn({ disabled, onClick, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      style={{ background: disabled ? C.card2 : C.orange, color: disabled ? C.t3 : '#FFFFFF', border: 'none', borderRadius: 10, padding: '9px 16px', fontFamily: font, fontSize: 13, fontWeight: 700, cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
      {children}
    </button>
  );
}

export default InstructorVideoQueue;
